import React, { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/api/client';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';
import { Search, Barcode, ShoppingCart, Wrench, AlertTriangle, Package, Cpu, ChevronRight, Truck } from 'lucide-react';

const TYPE_ICON = {
  GRN: <Package size={14} className="text-blue-600" />,
  SALE: <ShoppingCart size={14} className="text-emerald-600" />,
  BUILD: <Cpu size={14} className="text-purple-600" />,
  REPAIR: <Wrench size={14} className="text-amber-600" />,
  RMA: <AlertTriangle size={14} className="text-red-600" />,
  SUPPLIER: <Truck size={14} className="text-indigo-600" />,
};

const TYPE_COLOR = {
  GRN: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
  SALE: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
  BUILD: 'border-purple-500 bg-purple-50 dark:bg-purple-900/20',
  REPAIR: 'border-amber-500 bg-amber-50 dark:bg-amber-900/20',
  RMA: 'border-red-500 bg-red-50 dark:bg-red-900/20',
  SUPPLIER: 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20',
};

const statusBadge = (s) => {
  switch (s) {
    case 'In Stock': return 'success';
    case 'Sold': return 'info';
    case 'Reserved': return 'warning';
    case 'Used in Repair': return 'warning';
    case 'Sent for RMA': return 'danger';
    case 'With Supplier (RMA)': return 'warning';
    case 'Allocated to Repair Ticket': return 'info';
    case 'Reserved for RMA': return 'info';
    case 'Replaced': return 'default';
    case 'Replaced by Supplier': return 'default';
    case 'Credited by Supplier': return 'info';
    case 'Defective': return 'danger';
    case 'Dead': return 'danger';
    default: return 'default';
  }
};

export const SerialLookup = () => {
  const currency = useCurrency();
  const [sn, setSn] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const doLookup = async (e) => {
    e?.preventDefault?.();
    const q = sn.trim();
    if (!q) return;
    setLoading(true); setError(''); setData(null);
    try {
      const res = await api.traceSerial(q);
      if (!res.data.found) {
        setError(`No record found for "${q}"`);
      } else {
        setData(res.data);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Lookup failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="serial-lookup-page">
      <div>
        <h1 className="text-3xl font-bold">Serial Number Lookup</h1>
        <p className="text-sm text-[var(--text-muted)]">Type or scan any serial — see its full journey from supplier → stock → sale → repair → RMA.</p>
      </div>

      <Card>
        <form onSubmit={doLookup} className="flex gap-2">
          <div className="flex-1 relative">
            <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              value={sn}
              onChange={(e) => setSn(e.target.value)}
              placeholder="Scan barcode or type serial number (e.g. RAM001)..."
              className="pl-10 text-lg font-mono"
              autoFocus
              data-testid="sn-input"
            />
          </div>
          <Button type="submit" disabled={loading || !sn.trim()} data-testid="sn-search-btn">
            <Search size={16} className="mr-1" /> {loading ? 'Looking up...' : 'Lookup'}
          </Button>
        </form>
        {error && <p className="text-sm text-red-600 mt-3" data-testid="sn-error">{error}</p>}
      </Card>

      {data && (
        <>
          {/* Header card */}
          <Card>
            <div className="flex justify-between items-start flex-wrap gap-3">
              <div>
                <div className="font-mono text-2xl font-bold" data-testid="sn-result">{data.serial}</div>
                {data.product ? (
                  <>
                    <div className="text-lg font-semibold mt-1">{data.product.name}</div>
                    <div className="text-xs text-[var(--text-muted)]">
                      {[data.product.brand, data.product.model, data.product.category].filter(Boolean).join(' · ')}
                      {data.product.sku && <span> · SKU {data.product.sku}</span>}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-[var(--text-muted)] italic">Product record not found.</div>
                )}
              </div>
              <div className="text-right">
                <div className="text-xs text-[var(--text-muted)] uppercase">Current Status</div>
                {data.current_status
                  ? <Badge variant={statusBadge(data.current_status)} data-testid="sn-status">{data.current_status}</Badge>
                  : <span className="text-xs italic">unknown</span>}
              </div>
            </div>

            {/* Provenance summary */}
            {data.purchase_meta && (data.purchase_meta.supplier_name || data.purchase_meta.buy_cost) && (() => {
              const wm = parseInt(data.purchase_meta.warranty_months) || 0;
              // Warranty starts from sale date if sold, otherwise from GRN received date
              let warrantyStartRaw = null;
              let warrantyStartLabel = '';
              if (data.sale?.date) {
                warrantyStartRaw = data.sale.date;
                warrantyStartLabel = `Sale ${data.sale.date}`;
              } else if (data.purchase_meta.received_date) {
                warrantyStartRaw = data.purchase_meta.received_date;
                warrantyStartLabel = `GRN ${data.purchase_meta.received_date}`;
              }
              let expiryLabel = '—';
              let daysRemaining = null;
              if (wm > 0 && warrantyStartRaw) {
                const start = new Date(warrantyStartRaw);
                if (!isNaN(start)) {
                  const exp = new Date(start);
                  exp.setMonth(exp.getMonth() + wm);
                  expiryLabel = exp.toISOString().slice(0, 10);
                  daysRemaining = Math.ceil((exp - new Date()) / (1000 * 60 * 60 * 24));
                }
              }
              const warrantyLabel = wm > 0
                ? (wm % 12 === 0 ? `${wm / 12} year${wm === 12 ? '' : 's'}` : `${wm} months`)
                : 'No warranty';
              return (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 pt-4 border-t border-[var(--border)] text-xs">
                  <div><div className="text-[var(--text-muted)]">Supplier</div><div className="font-semibold">{data.purchase_meta.supplier_name || '—'}</div></div>
                  <div><div className="text-[var(--text-muted)]">Buy Cost</div><div className="font-semibold">{formatCurrency(data.purchase_meta.buy_cost || 0, currency)}</div></div>
                  <div><div className="text-[var(--text-muted)]">Condition</div><div className="font-semibold">{data.purchase_meta.condition || 'New'}</div></div>
                  <div><div className="text-[var(--text-muted)]">Warranty</div><div className="font-semibold">{warrantyLabel}{warrantyStartLabel ? <span className="block text-[10px] text-[var(--text-muted)]">from {warrantyStartLabel}</span> : null}</div></div>
                  <div>
                    <div className="text-[var(--text-muted)]">Warranty Expires</div>
                    <div className="font-semibold">
                      {expiryLabel}
                      {daysRemaining !== null && (
                        <span className={`ml-2 text-[10px] font-normal px-1.5 py-0.5 rounded ${daysRemaining < 0 ? 'bg-red-100 text-red-700' : daysRemaining < 30 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`} data-testid="sn-warranty-status">
                          {daysRemaining < 0 ? `Expired ${Math.abs(daysRemaining)}d ago` : `${daysRemaining}d left`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {data.sale && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-[var(--border)] text-xs">
                <div><div className="text-[var(--text-muted)]">Sold To</div><div className="font-semibold">{data.sale.customer_name || '—'}</div></div>
                <div><div className="text-[var(--text-muted)]">Sale Date</div><div className="font-semibold">{data.sale.date || '—'}</div></div>
                <div><div className="text-[var(--text-muted)]">Selling Price</div><div className="font-semibold">{formatCurrency(data.sale.selling_price || 0, currency)}</div></div>
                <div><div className="text-[var(--text-muted)]">Invoice</div><div className="font-mono text-xs font-semibold">{String(data.sale.invoice_id || '').slice(0,8).toUpperCase()}</div></div>
              </div>
            )}
          </Card>

          {/* Timeline */}
          <Card>
            <h3 className="text-sm font-semibold mb-3">Timeline</h3>
            {(data.timeline || []).length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] text-center py-6">No events recorded.</p>
            ) : (
              <div className="space-y-3" data-testid="sn-timeline">
                {data.timeline.map((ev, idx) => (
                  <div key={idx} className={`border-l-4 pl-3 py-2 pr-2 rounded ${TYPE_COLOR[ev.type] || ''}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        {TYPE_ICON[ev.type]} {ev.title}
                      </div>
                      <span className="text-xs text-[var(--text-muted)]">{ev.date || '—'}</span>
                    </div>
                    <div className="text-xs mt-1 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                      {Object.entries(ev.details || {}).map(([k, v]) => {
                        if (v === '' || v === 0 || v === null || v === undefined) return null;
                        if (typeof v === 'object') return null;
                        return (
                          <div key={k}><span className="text-[var(--text-muted)]">{k.replace(/_/g, ' ')}:</span> <strong>{String(v)}</strong></div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Related RMAs */}
          {(data.rmas || []).length > 0 && (
            <Card>
              <h3 className="text-sm font-semibold mb-3">Related RMAs ({data.rmas.length})</h3>
              <ul className="space-y-2">
                {data.rmas.map((r) => (
                  <li key={r.id} className="flex items-center justify-between text-xs border-b border-[var(--border)] pb-2">
                    <div>
                      <span className="font-mono font-semibold">{r.job_id || r.id.slice(0,8).toUpperCase()}</span>
                      <span className="ml-2 text-[var(--text-muted)]">{r.rma_type || 'Customer'} · {r.issue?.slice(0, 60) || '—'}</span>
                    </div>
                    <Badge variant="default">{r.status}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Related Builds */}
          {(data.builds || []).length > 0 && (
            <Card>
              <h3 className="text-sm font-semibold mb-3">Used in Builds ({data.builds.length})</h3>
              <ul className="space-y-2">
                {data.builds.map((b) => (
                  <li key={b.id} className="flex items-center justify-between text-xs border-b border-[var(--border)] pb-2">
                    <div className="flex items-center gap-2">
                      <Cpu size={14} className="text-purple-600" />
                      <span className="font-mono font-semibold">{b.build_number}</span>
                      <span className="text-[var(--text-muted)]">{b.build_name}</span>
                    </div>
                    <Badge variant="default">{b.status}</Badge>
                    <ChevronRight size={14} className="text-[var(--text-muted)]" />
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
